import _regeneratorRuntime from "/codebuild/output/src4243671113/src/node_modules/babel-preset-react-app/node_modules/@babel/runtime/regenerator";
import _createForOfIteratorHelper from "/codebuild/output/src4243671113/src/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/createForOfIteratorHelper";
import _classCallCheck from "/codebuild/output/src4243671113/src/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/classCallCheck";
import _createClass from "/codebuild/output/src4243671113/src/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/createClass";
import _inherits from "/codebuild/output/src4243671113/src/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/inherits";
import _createSuper from "/codebuild/output/src4243671113/src/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/createSuper";
/**
 * @license Copyright (c) 2003-2024, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
 */
/**
 * @module engine/view/selection
 */
import TypeCheckable from './typecheckable.js';
import Range from './range.js';
import Position from './position.js';
import Node from './node.js';
import DocumentSelection from './documentselection.js';
import { CKEditorError, EmitterMixin, count, isIterable } from '@ckeditor/ckeditor5-utils';
/**
 * Class representing an arbirtary selection in the view.
 * See also {@link module:engine/view/documentselection~DocumentSelection}.
 *
 * New selection instances can be created via the constructor or one these methods:
 *
 * * {@link module:engine/view/view~View#createSelection `View#createSelection()`},
 * * {@link module:engine/view/upcastwriter~UpcastWriter#createSelection `UpcastWriter#createSelection()`}.
 *
 * A selection can consist of {@link module:engine/view/range~Range ranges} that can be set by using
 * the {@link module:engine/view/selection~Selection#setTo `Selection#setTo()`} method.
 */
var Selection = /*#__PURE__*/function (_EmitterMixin) {
  _inherits(Selection, _EmitterMixin);
  var _super = _createSuper(Selection);
  /**
   * Creates new selection instance.
   *
   * **Note**: The selection constructor is available as a factory method:
   *
   * * {@link module:engine/view/view~View#createSelection `View#createSelection()`},
   * * {@link module:engine/view/upcastwriter~UpcastWriter#createSelection `UpcastWriter#createSelection()`}.
   *
   * ```ts
   * // Creates empty selection without ranges.
   * const selection = writer.createSelection();
   *
   * // Creates selection at the given range.
   * const range = writer.createRange( start, end );
   * const selection = writer.createSelection( range );
   *
   * // Creates selection at the given ranges
   * const ranges = [ writer.createRange( start1, end2 ), writer.createRange( star2, end2 ) ];
   * const selection = writer.createSelection( ranges );
   *
   * // Creates selection from the other selection.
   * const otherSelection = writer.createSelection();
   * const selection = writer.createSelection( otherSelection );
   *
   * // Creates selection from the document selection.
   * const selection = writer.createSelection( editor.editing.view.document.selection );
   *
   * // Creates selection at the given position.
   * const position = writer.createPositionFromPath( root, path );
   * const selection = writer.createSelection( position );
   *
   * // Creates collapsed selection at the position of given item and offset.
   * const paragraph = writer.createContainerElement( 'paragraph' );
   * const selection = writer.createSelection( paragraph, offset );
   *
   * // Creates a range inside an {@link module:engine/view/element~Element element} which starts before the
   * // first child of that element and ends after the last child of that element.
   * const selection = writer.createSelection( paragraph, 'in' );
   *
   * // Creates a range on an {@link module:engine/view/item~Item item} which starts before the item and ends
   * // just after the item.
   * const selection = writer.createSelection( paragraph, 'on' );
   * ```
   *
   * `Selection`'s constructor allow passing additional options (`backward`, `fake` and `label`) as the last argument.
   *
   * ```ts
   * // Creates backward selection.
   * const selection = writer.createSelection( range, { backward: true } );
   * ```
   *
   * Fake selection does not render as browser native selection over selected elements and is hidden to the user.
   * This way, no native selection UI artifacts are displayed to the user and selection over elements can be
   * represented in other way, for example by applying proper CSS class.
   *
   * Additionally fake's selection label can be provided. It will be used to describe fake selection in DOM
   * (and be  properly handled by screen readers).
   *
   * ```ts
   * // Creates fake selection with label.
   * const selection = writer.createSelection( range, { fake: true, label: 'foo' } );
   * ```
   *
   * @internal
   */
  function Selection() {
    var _this;
    _classCallCheck(this, Selection);
    _this = _super.call(this);
    _this._ranges = [];
    _this._lastRangeBackward = false;
    _this._isFake = false;
    _this._fakeSelectionLabel = '';
    if (arguments.length) {
      var _this2;
      (_this2 = _this).setTo.apply(_this2, arguments);
    }
    return _this;
  }
  /**
   * Returns true if selection instance is marked as `fake`.
   *
   * @see #setTo
   */
  _createClass(Selection, [{
    key: "isFake",
    get: function get() {
      return this._isFake;
    }
    /**
     * Returns fake selection label.
     *
     * @see #setTo
     */
  }, {
    key: "fakeSelectionLabel",
    get: function get() {
      return this._fakeSelectionLabel;
    }
    /**
     * Selection anchor. Anchor may be described as a position where the selection starts. Together with
     * {@link #focus focus} they define the direction of selection, which is important
     * when expanding/shrinking selection. Anchor is always the start or end of the most recent added range.
     * It may be a bit unintuitive when there are multiple ranges in selection.
     *
     * @see #focus
     */
  }, {
    key: "anchor",
    get: function get() {
      if (!this._ranges.length) {
        return null;
      }
      var range = this._ranges[this._ranges.length - 1];
      var anchor = this._lastRangeBackward ? range.end : range.start;
      return anchor.clone();
    }
    /**
     * Selection focus. Focus is a position where the selection ends.
     *
     * @see #anchor
     */
  }, {
    key: "focus",
    get: function get() {
      if (!this._ranges.length) {
        return null;
      }
      var range = this._ranges[this._ranges.length - 1];
      var focus = this._lastRangeBackward ? range.start : range.end;
      return focus.clone();
    }
    /**
     * Returns whether the selection is collapsed. Selection is collapsed when there is exactly one range which is
     * collapsed.
     */
  }, {
    key: "isCollapsed",
    get: function get() {
      return this.rangeCount === 1 && this._ranges[0].isCollapsed;
    }
    /**
     * Returns number of ranges in selection.
     */
  }, {
    key: "rangeCount",
    get: function get() {
      return this._ranges.length;
    }
    /**
     * Specifies whether the {@link #focus} precedes {@link #anchor}.
     */
  }, {
    key: "isBackward",
    get: function get() {
      return !this.isCollapsed && this._lastRangeBackward;
    }
    /**
     * {@link module:engine/view/editableelement~EditableElement EditableElement} instance that contains this selection, or `null`
     * if the selection is not inside an editable element.
     */
  }, {
    key: "editableElement",
    get: function get() {
      if (this.anchor) {
        return this.anchor.editableElement;
      }
      return null;
    }
    /**
     * Returns an iterable that contains copies of all ranges added to the selection.
     */
  }, {
    key: "getRanges",
    value:
    /*#__PURE__*/
    _regeneratorRuntime.mark(function getRanges() {
      var _iterator, _step, range;
      return _regeneratorRuntime.wrap(function getRanges$(_context) {
        while (1) {
          switch (_context.prev = _context.next) {
            case 0:
              _iterator = _createForOfIteratorHelper(this._ranges);
              _context.prev = 1;
              _iterator.s();
            case 3:
              if ((_step = _iterator.n()).done) {
                _context.next = 9;
                break;
              }
              range = _step.value;
              _context.next = 7;
              return range.clone();
            case 7:
              _context.next = 3;
              break;
            case 9:
              _context.next = 14;
              break;
            case 11:
              _context.prev = 11;
              _context.t0 = _context["catch"](1);
              _iterator.e(_context.t0);
            case 14:
              _context.prev = 14;
              _iterator.f();
              return _context.finish(14);
            case 17:
            case "end":
              return _context.stop();
          }
        }
      }, getRanges, this, [[1, 11, 14, 17]]);
    })
    /**
     * Returns copy of the first range in the selection. First range is the one which
     * {@link module:engine/view/range~Range#start start} position {@link module:engine/view/position~Position#isBefore is before} start
     * position of all other ranges (not to confuse with the first range added to the selection).
     * Returns `null` if no ranges are added to selection.
     */
  }, {
    key: "getFirstRange",
    value: function getFirstRange() {
      var first = null;
      var _iterator2 = _createForOfIteratorHelper(this._ranges),
        _step2;
      try {
        for (_iterator2.s(); !(_step2 = _iterator2.n()).done;) {
          var range = _step2.value;
          if (!first || range.start.isBefore(first.start)) {
            first = range;
          }
        }
      } catch (err) {
        _iterator2.e(err);
      } finally {
        _iterator2.f();
      }
      return first ? first.clone() : null;
    }
    /**
     * Returns copy of the last range in the selection. Last range is the one which {@link module:engine/view/range~Range#end end}
     * position {@link module:engine/view/position~Position#isAfter is after} end position of all other ranges (not to confuse
     * with the last range added to the selection). Returns `null` if no ranges are added to selection.
     */
  }, {
    key: "getLastRange",
    value: function getLastRange() {
      var last = null;
      var _iterator3 = _createForOfIteratorHelper(this._ranges),
        _step3;
      try {
        for (_iterator3.s(); !(_step3 = _iterator3.n()).done;) {
          var range = _step3.value;
          if (!last || range.end.isAfter(last.end)) {
            last = range;
          }
        }
      } catch (err) {
        _iterator3.e(err);
      } finally {
        _iterator3.f();
      }
      return last ? last.clone() : null;
    }
    /**
     * Returns copy of the first position in the selection. First position is the position that
     * {@link module:engine/view/position~Position#isBefore is before} any other position in the selection ranges.
     * Returns `null` if no ranges are added to selection.
     */
  }, {
    key: "getFirstPosition",
    value: function getFirstPosition() {
      var firstRange = this.getFirstRange();
      return firstRange ? firstRange.start.clone() : null;
    }
    /**
     * Returns copy of the last position in the selection. Last position is the position that
     * {@link module:engine/view/position~Position#isAfter is after} any other position in the selection ranges.
     * Returns `null` if no ranges are added to selection.
     */
  }, {
    key: "getLastPosition",
    value: function getLastPosition() {
      var lastRange = this.getLastRange();
      return lastRange ? lastRange.end.clone() : null;
    }
    /**
     * Checks whether, this selection is equal to given selection. Selections are equal if they have same directions,
     * same number of ranges and all ranges from one selection equal to a range from other selection.
     *
     * @param otherSelection Selection to compare with.
     * @returns `true` if selections are equal, `false` otherwise.
     */
  }, {
    key: "isEqual",
    value: function isEqual(otherSelection) {
      if (this.isFake != otherSelection.isFake) {
        return false;
      }
      if (this.isFake && this.fakeSelectionLabel != otherSelection.fakeSelectionLabel) {
        return false;
      }
      if (this.rangeCount != otherSelection.rangeCount) {
        return false;
      } else if (this.rangeCount === 0) {
        return true;
      }
      if (!this.anchor.isEqual(otherSelection.anchor) || !this.focus.isEqual(otherSelection.focus)) {
        return false;
      }
      var _iterator4 = _createForOfIteratorHelper(this._ranges),
        _step4;
      try {
        for (_iterator4.s(); !(_step4 = _iterator4.n()).done;) {
          var thisRange = _step4.value;
          var found = false;
          var _iterator5 = _createForOfIteratorHelper(otherSelection._ranges),
            _step5;
          try {
            for (_iterator5.s(); !(_step5 = _iterator5.n()).done;) {
              var otherRange = _step5.value;
              if (thisRange.isEqual(otherRange)) {
                found = true;
                break;
              }
            }
          } catch (err) {
            _iterator5.e(err);
          } finally {
            _iterator5.f();
          }
          if (!found) {
            return false;
          }
        }
      } catch (err) {
        _iterator4.e(err);
      } finally {
        _iterator4.f();
      }
      return true;
    }
    /**
     * Checks whether this selection is similar to given selection. Selections are similar if they have same directions, same
     * number of ranges, and all {@link module:engine/view/range~Range#getTrimmed trimmed} ranges from one selection are
     * equal to any trimmed range from other selection.
     *
     * @param otherSelection Selection to compare with.
     * @returns `true` if selections are similar, `false` otherwise.
     */
  }, {
    key: "isSimilar",
    value: function isSimilar(otherSelection) {
      if (this.isBackward != otherSelection.isBackward) {
        return false;
      }
      var numOfRangesA = count(this.getRanges());
      var numOfRangesB = count(otherSelection.getRanges());
      // If selections have different number of ranges, they cannot be similar.
      if (numOfRangesA != numOfRangesB) {
        return false;
      }
      // If both selections have no ranges, they are similar.
      if (numOfRangesA == 0) {
        return true;
      }
      // Check if each range in one selection has a similar range in other selection.
      var _iterator6 = _createForOfIteratorHelper(this.getRanges()),
        _step6;
      try {
        for (_iterator6.s(); !(_step6 = _iterator6.n()).done;) {
          var rangeA = _step6.value;
          rangeA = rangeA.getTrimmed();
          var found = false;
          var _iterator7 = _createForOfIteratorHelper(otherSelection.getRanges()),
            _step7;
          try {
            for (_iterator7.s(); !(_step7 = _iterator7.n()).done;) {
              var rangeB = _step7.value;
              rangeB = rangeB.getTrimmed();
              if (rangeA.start.isEqual(rangeB.start) && rangeA.end.isEqual(rangeB.end)) {
                found = true;
                break;
              }
            }
            // For `rangeA`, neither range in `otherSelection` was similar. So selections are not similar.
          } catch (err) {
            _iterator7.e(err);
          } finally {
            _iterator7.f();
          }
          if (!found) {
            return false;
          }
        }
        // There were no ranges that weren't matched. Selections are similar.
      } catch (err) {
        _iterator6.e(err);
      } finally {
        _iterator6.f();
      }
      return true;
    }
    /**
     * Returns the selected element. {@link module:engine/view/element~Element Element} is considered as selected if there is only
     * one range in the selection, and that range contains exactly one element.
     * Returns `null` if there is no selected element.
     */
  }, {
    key: "getSelectedElement",
    value: function getSelectedElement() {
      if (this.rangeCount !== 1) {
        return null;
      }
      return this.getFirstRange().getContainedElement();
    }
    /**
     * Sets this selection's ranges and direction to the specified location based on the given
     * {@link module:engine/view/selection~Selectable selectable}.
     *
     * ```ts
     * // Sets selection to the given range.
     * const range = writer.createRange( start, end );
     * selection.setTo( range );
     *
     * // Sets selection to given ranges.
     * const ranges = [ writer.createRange( start1, end2 ), writer.createRange( star2, end2 ) ];
     * selection.setTo( range );
     *
     * // Sets selection to the other selection.
     * const otherSelection = writer.createSelection();
     * selection.setTo( otherSelection );
     *
     * // Sets selection to contents of DocumentSelection.
     * selection.setTo( editor.editing.view.document.selection );
     *
     * // Sets collapsed selection at the given position.
     * const position = writer.createPositionAt( root, path );
     * selection.setTo( position );
     *
     * // Sets collapsed selection at the position of given item and offset.
     * selection.setTo( paragraph, offset );
     * ```
     *
     * Creates a range inside an {@link module:engine/view/element~Element element} which starts before the first child of
     * that element and ends after the last child of that element.
     *
     * ```ts
     * selection.setTo( paragraph, 'in' );
     * ```
     *
     * Creates a range on an {@link module:engine/view/item~Item item} which starts before the item and ends just after the item.
     *
     * ```ts
     * selection.setTo( paragraph, 'on' );
     *
     * // Clears selection. Removes all ranges.
     * selection.setTo( null );
     * ```
     *
     * `Selection#setTo()` method allow passing additional options (`backward`, `fake` and `label`) as the last argument.
     *
     * ```ts
     * // Sets selection as backward.
     * selection.setTo( range, { backward: true } );
     * ```
     *
     * Fake selection does not render as browser native selection over selected elements and is hidden to the user.
     * This way, no native selection UI artifacts are displayed to the user and selection over elements can be
     * represented in other way, for example by applying proper CSS class.
     *
     * Additionally fake's selection label can be provided. It will be used to describe fake selection in DOM
     * (and be  properly handled by screen readers).
     *
     * ```ts
     * // Creates fake selection with label.
     * selection.setTo( range, { fake: true, label: 'foo' } );
     * ```
     *
     * @fires change
     */
  }, {
    key: "setTo",
    value: function setTo() {
      for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
        args[_key] = arguments[_key];
      }
      var selectable = args[0],
        placeOrOffset = args[1],
        options = args[2];
      if (typeof placeOrOffset == 'object') {
        options = placeOrOffset;
        placeOrOffset = undefined;
      }
      if (selectable === null) {
        this._setRanges([]);
        this._setFakeOptions(options);
      } else if (selectable instanceof Selection || selectable instanceof DocumentSelection) {
        this._setRanges(selectable.getRanges(), selectable.isBackward);
        this._setFakeOptions({
          fake: selectable.isFake,
          label: selectable.fakeSelectionLabel
        });
      } else if (selectable instanceof Range) {
        this._setRanges([selectable], options && options.backward);
        this._setFakeOptions(options);
      } else if (selectable instanceof Position) {
        this._setRanges([new Range(selectable)]);
        this._setFakeOptions(options);
      } else if (selectable instanceof Node) {
        var backward = !!options && !!options.backward;
        var range;
        if (placeOrOffset === undefined) {
          /**
           * selection.setTo requires the second parameter when the first parameter is a node.
           *
           * @error view-selection-setto-required-second-parameter
           */
          throw new CKEditorError('view-selection-setto-required-second-parameter', this);
        } else if (placeOrOffset == 'in') {
          range = Range._createIn(selectable);
        } else if (placeOrOffset == 'on') {
          range = Range._createOn(selectable);
        } else {
          range = new Range(Position._createAt(selectable, placeOrOffset));
        }
        this._setRanges([range], backward);
        this._setFakeOptions(options);
      } else if (isIterable(selectable)) {
        // We assume that the selectable is an iterable of ranges.
        // Array.from() is used to prevent setting ranges to the old iterable
        this._setRanges(selectable, options && options.backward);
        this._setFakeOptions(options);
      } else {
        /**
         * Cannot set selection to given place.
         *
         * @error view-selection-setto-not-selectable
         */
        throw new CKEditorError('view-selection-setto-not-selectable', this);
      }
      this.fire('change');
    }
    /**
     * Moves {@link #focus} to the specified location.
     *
     * The location can be specified in the same form as {@link module:engine/view/view~View#createPositionAt view.createPositionAt()}
     * parameters.
     *
     * @fires change
     * @param offset Offset or one of the flags. Used only when first parameter is a {@link module:engine/view/item~Item view item}.
     */
  }, {
    key: "setFocus",
    value: function setFocus(itemOrPosition, offset) {
      if (this.anchor === null) {
        /**
         * Cannot set selection focus if there are no ranges in selection.
         *
         * @error view-selection-setfocus-no-ranges
         */
        throw new CKEditorError('view-selection-setfocus-no-ranges', this);
      }
      var newFocus = Position._createAt(itemOrPosition, offset);
      if (newFocus.compareWith(this.focus) == 'same') {
        return;
      }
      var anchor = this.anchor;
      this._ranges.pop();
      if (newFocus.compareWith(anchor) == 'before') {
        this._addRange(new Range(newFocus, anchor), true);
      } else {
        this._addRange(new Range(anchor, newFocus));
      }
      this.fire('change');
    }
    /**
     * Replaces all ranges that were added to the selection with given array of ranges. Last range of the array
     * is treated like the last added range and is used to set {@link #anchor anchor} and {@link #focus focus}.
     * Accepts a flag describing in which way the selection is made.
     *
     * @param newRanges Iterable object of ranges to set.
     * @param isLastBackward Flag describing if last added range was selected forward - from start to end
     * (`false`) or backward - from end to start (`true`). Defaults to `false`.
     */
  }, {
    key: "_setRanges",
    value: function _setRanges(newRanges) {
      var isLastBackward = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
      // New ranges should be copied to prevent removing them by setting them to `[]` first.
      // Only applies to situations when selection is set to the same selection or same selection's ranges.
      newRanges = Array.from(newRanges);
      this._ranges = [];
      var _iterator8 = _createForOfIteratorHelper(newRanges),
        _step8;
      try {
        for (_iterator8.s(); !(_step8 = _iterator8.n()).done;) {
          var range = _step8.value;
          this._addRange(range);
        }
      } catch (err) {
        _iterator8.e(err);
      } finally {
        _iterator8.f();
      }
      this._lastRangeBackward = !!isLastBackward;
    }
    /**
     * Sets this selection instance to be marked as `fake`. A fake selection does not render as browser native selection
     * over selected elements and is hidden to the user. This way, no native selection UI artifacts are displayed to
     * the user and selection over elements can be represented in other way, for example by applying proper CSS class.
     *
     * Additionally fake's selection label can be provided. It will be used to describe fake selection in DOM (and be
     * properly handled by screen readers).
     */
  }, {
    key: "_setFakeOptions",
    value: function _setFakeOptions() {
      var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
      this._isFake = !!options.fake;
      this._fakeSelectionLabel = options.fake ? options.label || '' : '';
    }
    /**
     * Adds a range to the selection. Added range is copied. This means that passed range is not saved in the
     * selection instance and you can safely operate on it.
     *
     * Accepts a flag describing in which way the selection is made - passed range might be selected from
     * {@link module:engine/view/range~Range#start start} to {@link module:engine/view/range~Range#end end}
     * or from {@link module:engine/view/range~Range#end end} to {@link module:engine/view/range~Range#start start}.
     * The flag is used to set {@link #anchor anchor} and {@link #focus focus} properties.
     *
     * Throws {@link module:utils/ckeditorerror~CKEditorError CKEditorError} `view-selection-range-intersects` if added range intersects
     * with ranges already stored in Selection instance.
     */
  }, {
    key: "_addRange",
    value: function _addRange(range) {
      var isBackward = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
      if (!(range instanceof Range)) {
        /**
         * Selection range set to an object that is not an instance of {@link module:engine/view/range~Range}.
         *
         * @error view-selection-add-range-not-range
         */
        throw new CKEditorError('view-selection-add-range-not-range', this);
      }
      this._pushRange(range);
      this._lastRangeBackward = !!isBackward;
    }
    /**
     * Adds range to selection - creates copy of given range so it can be safely used and modified.
     *
     * Throws {@link module:utils/ckeditorerror~CKEditorError CKEditorError} `view-selection-range-intersects` if added range intersects
     * with ranges already stored in selection instance.
     */
  }, {
    key: "_pushRange",
    value: function _pushRange(range) {
      var _iterator9 = _createForOfIteratorHelper(this._ranges),
        _step9;
      try {
        for (_iterator9.s(); !(_step9 = _iterator9.n()).done;) {
          var storedRange = _step9.value;
          if (range.isIntersecting(storedRange)) {
            /**
             * Trying to add a range that intersects with another range from selection.
             *
             * @error view-selection-range-intersects
             * @param {module:engine/view/range~Range} addedRange Range that was added to the selection.
             * @param {module:engine/view/range~Range} intersectingRange Range from selection that intersects with `addedRange`.
             */
            throw new CKEditorError('view-selection-range-intersects', this, {
              addedRange: range,
              intersectingRange: storedRange
            });
          }
        }
      } catch (err) {
        _iterator9.e(err);
      } finally {
        _iterator9.f();
      }
      this._ranges.push(new Range(range.start, range.end));
    }
  }]);
  return Selection;
}(EmitterMixin(TypeCheckable)); // The magic of type inference using `is` method is centralized in `TypeCheckable` class.
// Proper overload would interfere with that.
export { Selection as default };
Selection.prototype.is = function (type) {
  return type === 'selection' || type === 'view:selection';
};